import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };
      if (formData.message && formData.message.trim()) {
        payload.message = formData.message;
      }
      await axios.post(`${API}/contact`, payload);
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-spacing bg-white relative overflow-hidden" id="contato">
      {/* Decorative organic shapes */}
      <div className="absolute top-0 left-0 w-80 h-80 opacity-[0.03]" style={{
        background: 'radial-gradient(ellipse at center, #11830f 0%, transparent 65%)',
        borderRadius: '60% 40% 50% 50% / 40% 60% 40% 60%',
        transform: 'translate(-40%, -40%)'
      }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 opacity-[0.04]" style={{
        background: 'radial-gradient(ellipse at center, #11830f 0%, transparent 60%)',
        borderRadius: '40% 60% 30% 70% / 50% 30% 70% 50%',
        transform: 'translate(30%, 30%)'
      }} />
      {/* Dotted accent */}
      <div className="absolute top-20 right-20 opacity-[0.06]">
        <svg width="120" height="120" viewBox="0 0 120 120">
          {[...Array(6)].map((_, row) =>
            [...Array(6)].map((_, col) => (
              <circle key={`${row}-${col}`} cx={col * 20 + 10} cy={row * 20 + 10} r="2" fill="#11830f" />
            ))
          )}
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A1A] mb-6">
            Agende sua consulta
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Entre em contato e dê o primeiro passo para transformar sua saúde
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl md:text-3xl font-medium text-[#1A1A1A] mb-6">
                Informações de contato
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <a href="https://wa.me/5535988366028?text=Ol%C3%A1%20Thiago%2C%20tudo%20bem%3F%20Vim%20pelo%20seu%20site%20e%20gostaria%20de%20agendar%20uma%20consulta.%20Poderia%20me%20informar%20sobre%20os%20hor%C3%A1rios%20dispon%C3%ADveis%20e%20os%20planos%20de%20acompanhamento%3F%20Desde%20j%C3%A1%2C%20agrade%C3%A7o!" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center flex-shrink-0 hover:bg-[#0d6a0c] transition-colors">
                    <Phone className="w-6 h-6 text-white" />
                  </a>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">WhatsApp</p>
                    <a href="https://wa.me/5535988366028?text=Ol%C3%A1%20Thiago%2C%20tudo%20bem%3F%20Vim%20pelo%20seu%20site%20e%20gostaria%20de%20agendar%20uma%20consulta.%20Poderia%20me%20informar%20sobre%20os%20hor%C3%A1rios%20dispon%C3%ADveis%20e%20os%20planos%20de%20acompanhamento%3F%20Desde%20j%C3%A1%2C%20agrade%C3%A7o!" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-brand-green transition-colors">(35) 98836-6028</a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-email">
                  <a href="mailto:thiagoferreiranutri@gmail.com" className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center flex-shrink-0 hover:bg-[#0d6a0c] transition-colors">
                    <Mail className="w-6 h-6 text-white" />
                  </a>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">E-mail</p>
                    <a href="mailto:thiagoferreiranutri@gmail.com" className="text-gray-600 hover:text-brand-green transition-colors">thiagoferreiranutri@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-instagram">
                  <a href="https://www.instagram.com/thiagoferreira_nutri" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center flex-shrink-0 hover:bg-[#0d6a0c] transition-colors">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Instagram</p>
                    <a href="https://www.instagram.com/thiagoferreira_nutri" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-brand-green transition-colors">@thiagoferreira_nutri</a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-location">
                  <a href="#localizacao" className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center flex-shrink-0 hover:bg-[#0d6a0c] transition-colors">
                    <MapPin className="w-6 h-6 text-white" />
                  </a>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] mb-1">Localização</p>
                    <p className="text-gray-600">Poços de Caldas - MG</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F9F7] rounded-2xl p-6">
              <h4 className="font-semibold text-[#1A1A1A] mb-3">Horário de Atendimento</h4>
              <div className="space-y-2 text-gray-600">
                <p>Segunda a Quinta: 8h às 20h</p>
                <p>Sexta: 8h às 17h</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="bg-[#F9F9F7] rounded-2xl p-8 space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Nome completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-form-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="contact-form-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Telefone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="contact-form-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="(11) 98765-4321"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Mensagem (opcional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-form-message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent resize-none"
                  placeholder="Conte um pouco sobre seus objetivos..."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="w-full bg-brand-green text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:bg-brand-green-dark hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
                <Send className="w-5 h-5" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;